/*
 * WYRDO cart + product web components (ST2.3 / ST2.4 / ST2.5).
 *
 * Progressive enhancement only: every element upgraded here works as a plain HTML
 * form/link without JS. These components add live variant matching, AJAX add-to-cart,
 * a slide-in cart drawer, quantity edits, a sticky mobile ATC, and the locked pack's code box.
 *
 * Cart state is shared through a tiny pub/sub (WyrdoCart). Any component can call
 * WyrdoCart.refresh() after a mutation; subscribers (count bubble, drawer, sticky bar)
 * re-render from the returned cart JSON. Section Rendering API keeps the drawer HTML
 * in sync without a full reload.
 */
(function () {
  'use strict';

  const money = (cents) =>
    (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents)
      : '$' + (cents / 100).toFixed(2);

  /* ----------------------------------------------------------------------
   * WyrdoCart — shared cart client + event bus
   * -------------------------------------------------------------------- */
  const listeners = new Set();
  const WyrdoCart = {
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    _emit(cart) { listeners.forEach((fn) => { try { fn(cart); } catch (e) { console.error(e); } }); },

    async get() {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      // a password-gated or proxied storefront can answer with HTML (the CLI dev server does, intermittently):
      // say so instead of letting res.json() throw a bare SyntaxError up to whoever asked for the count.
      // Shopify itself answers /cart.js as text/javascript, so accept json OR javascript, never only json.
      if (!res.ok || !/json|javascript/.test(res.headers.get('content-type') || '')) throw new Error('cart.js: ' + res.status + ' ' + (res.headers.get('content-type') || ''));
      return res.json();
    },

    /* Add a variant. `sections` = comma list to bundle in the Section Rendering API. */
    async add(id, quantity = 1, properties = null, sections = null) {
      const body = { items: [{ id, quantity }] };
      if (properties) body.items[0].properties = properties;
      if (sections) { body.sections = sections; body.sections_url = window.location.pathname; }
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { const err = new Error(data.description || 'Add to cart failed'); err.data = data; throw err; }
      await this.refresh();
      return data;
    },

    async change(line, quantity) {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      const cart = await res.json();
      this._emit(cart);
      return cart;
    },

    /* POST /cart/update.js — attributes, `updates` ({ variantId: qty }) and/or `discount` (the FULL comma list; '' clears). */
    async update(body) {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const cart = await res.json();
      this._emit(cart);
      return cart;
    },

    updateAttributes(attributes) { return this.update({ attributes }); },

    async refresh() { const cart = await this.get(); this._emit(cart); return cart; },
    formatMoney: money,
  };
  window.WyrdoCart = WyrdoCart;

  /* Cart count bubbles everywhere (header) stay live. */
  WyrdoCart.subscribe((cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const prev = parseInt(el.textContent, 10) || 0;
      el.textContent = cart.item_count;
      el.classList.toggle('is-empty', cart.item_count === 0);
      if (cart.item_count > prev) {                       // something landed: pop the cart square + bubble
        const sq = el.closest('.wyrdo-sq') || el;
        sq.classList.remove('is-pop'); void sq.offsetWidth; sq.classList.add('is-pop');
        sq.addEventListener('animationend', () => sq.classList.remove('is-pop'), { once: true });
      }
    });
  });

  /* ----------------------------------------------------------------------
   * <wyrdo-variant-picker> — resolve options -> variant, update UI
   * -------------------------------------------------------------------- */
  class WyrdoVariantPicker extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form') || document.getElementById(`product-form-${this.dataset.sectionId}`);
      this.productForm = this.closest('wyrdo-product-form');
      this.variants = this._readVariants();
      this.addEventListener('change', () => this.onChange());
      // Resolve once on load so price/media reflect the pre-checked options.
      this.onChange(true);
    }

    _readVariants() {
      const holder = (this.productForm || document).querySelector('[data-product-json]');
      if (!holder) return [];
      try { return JSON.parse(holder.textContent); } catch (e) { return []; }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked'))
        .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
        .map((r) => r.value);
    }

    match() {
      const chosen = this.selectedOptions;
      return this.variants.find((v) =>
        v.options.every((opt, i) => opt === chosen[i])
      );
    }

    onChange(initial) {
      const variant = this.match();
      this._markAvailability();
      const idInput = (this.productForm || this.form || document).querySelector('[data-variant-id]');
      if (variant && idInput) idInput.value = variant.id;

      this.dispatchEvent(new CustomEvent('wyrdo:variant-change', {
        bubbles: true, detail: { variant, initial: !!initial },
      }));
    }

    /* Grey out option values that would produce an unavailable variant. */
    _markAvailability() {
      const groups = Array.from(this.querySelectorAll('.wyrdo-variants__group'));
      groups.forEach((group, gi) => {
        group.querySelectorAll('input[type="radio"]').forEach((radio) => {
          const trial = this.selectedOptions.slice();
          trial[gi] = radio.value;
          const v = this.variants.find((vt) => vt.options.every((o, i) => o === trial[i]));
          const label = radio.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !(v && v.available));
        });
      });
    }
  }
  customElements.define('wyrdo-variant-picker', WyrdoVariantPicker);

  /* ----------------------------------------------------------------------
   * <wyrdo-product-form> — AJAX add-to-cart + live price/media/availability
   * -------------------------------------------------------------------- */
  class WyrdoProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.atc = this.querySelector('[data-atc]');
      this.atcLabel = this.querySelector('[data-atc-label]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.section = document.querySelector(`.wyrdo-product[data-section-id="${this.dataset.sectionId}"]`)
        || this.closest('.wyrdo-product') || document;

      this.addEventListener('wyrdo:variant-change', (e) => this.onVariant(e.detail));
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    onVariant({ variant }) {
      // Price
      const priceEl = this.section.querySelector('[data-price-block]');
      if (priceEl && variant) {
        const cur = priceEl.querySelector('[data-price]');
        const cmp = priceEl.querySelector('[data-compare]');
        const badge = priceEl.querySelector('[data-sale-badge]');
        if (cur) cur.textContent = money(variant.price);
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceEl.classList.toggle('wyrdo-price--sale', !!onSale);
        if (cmp) { cmp.textContent = money(variant.compare_at_price || 0); cmp.hidden = !onSale; }
        if (badge) badge.hidden = !onSale;
      }

      // Availability
      if (this.atc) {
        const avail = variant && variant.available;
        this.atc.disabled = !avail;
        this.atc.setAttribute('aria-disabled', String(!avail));
        if (this.atcLabel && !avail) this.atcLabel.textContent = this.section.dataset.soldout || 'Sold out';
      }

      // Media — reveal the matching gallery slide
      if (variant && variant.featured_media) {
        const gallery = this.section.querySelector('wyrdo-product-gallery');
        if (gallery && gallery.show) gallery.show(String(variant.featured_media.id));
      }

      // Reflect in the URL for shareable variant links (no reload).
      if (variant && window.history?.replaceState) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    }

    async onSubmit(e) {
      // No fetch support -> let the native form POST proceed.
      if (!window.fetch) return;
      e.preventDefault();
      if (!this.form || this.atc?.disabled) return;

      const id = this.form.querySelector('[data-variant-id]')?.value || this.form.querySelector('[name="id"]')?.value;
      const qtyEl = this.form.querySelector('[name="quantity"]');
      let qty = parseInt(qtyEl?.value || '1', 10);
      if (qtyEl?.max) qty = Math.min(qty, parseInt(qtyEl.max, 10));   // a locked pack is one personal copy (max="1"), typed values included
      if (!id) return;

      this._setBusy(true);
      this._clearError();
      try {
        await WyrdoCart.add(id, qty, null, 'cart-drawer');
        document.dispatchEvent(new CustomEvent('wyrdo:cart-added'));
      } catch (err) {
        this._showError(err.message || 'Could not add to cart.');
      } finally {
        this._setBusy(false);
      }
    }

    _setBusy(b) {
      if (!this.atc) return;
      this.atc.classList.toggle('is-busy', b);
      this.atc.setAttribute('aria-busy', String(b));
    }
    _showError(msg) { if (this.errorEl) { this.errorEl.textContent = msg; this.errorEl.hidden = false; } }
    _clearError() { if (this.errorEl) { this.errorEl.textContent = ''; this.errorEl.hidden = true; } }
  }
  customElements.define('wyrdo-product-form', WyrdoProductForm);

  /* ----------------------------------------------------------------------
   * <wyrdo-product-gallery> — thumbnail rail switching (no-JS = first slide shown)
   * -------------------------------------------------------------------- */
  class WyrdoProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.wyrdo-gallery__stage');
      this.track = this.querySelector('[data-gallery-track]');          // story PDP: scroll-snap slides + dots
      this.dots = Array.from(this.querySelectorAll('.wyrdo-pdp__dot'));
      this.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-thumb]');
        if (thumb) { e.preventDefault(); this.show(thumb.dataset.thumb); }
      });
      if (this.track) this.track.addEventListener('scroll', () => { if (!this._raf) this._raf = requestAnimationFrame(() => { this._raf = null; this._update(); }); }, { passive: true });
    }
    _update() {
      const w = this.track.clientWidth || 1, i = Math.round(this.track.scrollLeft / w);
      this.dots.forEach((d, j) => d.classList.toggle('is-on', j === i));
      // R9: the thumbnail rail under the story gallery follows the snapped slide (pressed-in look on the current one)
      this.querySelectorAll('[data-thumb]').forEach((t, j) => { t.classList.toggle('is-on', j === i); t.setAttribute('aria-current', j === i ? 'true' : 'false'); });
    }
    show(mediaId) {
      if (this.track) {
        const slide = this.querySelector('[data-media-id="' + mediaId + '"]');
        if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        return;
      }
      this.querySelectorAll('[data-media-id]').forEach((s) => { s.hidden = s.dataset.mediaId !== mediaId; });
      this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.toggle('is-active', t.dataset.thumb === mediaId));
    }
  }
  customElements.define('wyrdo-product-gallery', WyrdoProductGallery);

  /* ----------------------------------------------------------------------
   * Quantity steppers (buy box + drawer). Delegated, works on any [data-qty-up/down].
   * -------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const up = e.target.closest('[data-qty-up]');
    const down = e.target.closest('[data-qty-down]');
    if (!up && !down) return;
    const wrap = (up || down).closest('.wyrdo-qty');
    const input = wrap && wrap.querySelector('input[type="number"], .wyrdo-qty__input');
    if (!input) return;
    const min = parseInt(input.min || '1', 10);
    let val = parseInt(input.value || String(min), 10);
    val = up ? val + 1 : Math.max(min, val - 1);
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ----------------------------------------------------------------------
   * Quick-add (product cards in the collection grid / colorway carousel /
   * quiz result / search). Delegated over any [data-quick-add] button so the
   * gold "Add to Cart" adds the variant + opens/refreshes the drawer. The
   * drawer's OWN upsell buttons are handled separately (they live inside
   * <wyrdo-cart-drawer>), so skip those here to avoid a double-add. No-JS:
   * the card's PDP link carries the purchase.
   * -------------------------------------------------------------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn || btn.closest('wyrdo-cart-drawer')) return;
    e.preventDefault();
    if (btn.disabled) return;
    const id = btn.dataset.quickAdd;
    if (!id) return;
    btn.classList.add('is-busy');
    btn.disabled = true;
    try {
      // pass 'cart-drawer' so the drawer re-renders its markup on add
      await WyrdoCart.add(id, 1, null, 'cart-drawer');
      // open the drawer, mirroring the PDP add path (<wyrdo-product-form>)
      document.dispatchEvent(new CustomEvent('wyrdo:cart-added'));
    } catch (err) {
      /* WyrdoCart surfaces its own error state; re-enable so the user can retry */
    } finally {
      btn.classList.remove('is-busy');
      btn.disabled = false;
    }
  });

  /* ----------------------------------------------------------------------
   * Checkout pop-ups (snippets/free-shipping.liquid), each at most once a visit, in this order:
   * 1. "You forgot your free pack" — rendered when the cart has the base game but no free-pack
   *    choice; opened only if a code is waiting: /cart.js lists it as not applicable (the
   *    undocumented discount_codes, as <wyrdo-unlock> uses), or Shopify's discount-link cookie
   *    holds a PREORDER-/REF- code the cart hasn't taken yet.
   * 2. Free shipping — under the line, the add-a-pack pop-up.
   * A pop-up's own Checkout button submits the same form while it's open, so it goes straight
   * through. Nothing to show = checkout as normal.
   * -------------------------------------------------------------------- */
  const seen = (key) => { try { return !!sessionStorage.getItem(key); } catch (_) { return false; } };
  const mark = (key) => { try { sessionStorage.setItem(key, '1'); } catch (_) { /* blocked: the pop-up's own button still goes through */ } };
  const waitingCode = (cart) => {
    const codes = cart?.discount_codes || [];
    const unused = codes.find((c) => !c.applicable);
    if (unused) return unused.code;
    const cookie = decodeURIComponent((document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/) || [])[1] || '');
    return !codes.length && /^(PREORDER|REF)-/i.test(cookie) ? cookie : null;
  };
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (e.submitter?.name !== 'checkout' || form.dataset.checked || document.querySelector('dialog[open][data-pack-nudge], dialog[open][data-ship-nudge]')) return;
    const pack = !seen('wyrdo-pack-nudge') && document.querySelector(`[data-pack-nudge="${form.id}"]`);
    const ship = !seen('wyrdo-ship-nudge') && document.querySelector(`[data-ship-nudge="${form.id}"]`);
    if (!pack && !ship) return;
    e.preventDefault();
    let open = ship && [ship, 'wyrdo-ship-nudge'];
    if (pack) {
      const code = waitingCode(await WyrdoCart.get().catch(() => null));
      if (code) {
        pack.querySelectorAll('[data-pack-code]').forEach((el) => { el.textContent = code; });
        open = [pack, 'wyrdo-pack-nudge'];
      }
    }
    if (open) { mark(open[1]); open[0].showModal(); return; }
    form.dataset.checked = '1';         // nothing to say: carry on to checkout
    form.requestSubmit(e.submitter);
    delete form.dataset.checked;
  });

  /* ----------------------------------------------------------------------
   * <wyrdo-cart-drawer> — slide-in cart. Opens on add, refreshes via Section
   * Rendering API, handles line qty (/cart/change.js), gift note (cart attribute),
   * and the free-shipping pop-up's quick-add packs. No-JS: stays hidden; header cart icon links to /cart.
   * -------------------------------------------------------------------- */
  class WyrdoCartDrawer extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector('[data-drawer-content]');
      this.panel = this.querySelector('.wyrdo-drawer__panel');

      // Intercept the header cart link to open the drawer instead of navigating.
      document.querySelectorAll('.wyrdo-header__cart, [data-cart-toggle]').forEach((el) => {
        el.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
      });
      document.addEventListener('wyrdo:cart-added', () => this.open());

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); this.close(); }
      });
      // Esc inside a checkout pop-up closes only the pop-up
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.isOpen && !this.querySelector('dialog[open]')) this.close(); });

      this._wireContent();

      // Re-render the count bubble whenever the cart changes; also refresh drawer HTML.
      WyrdoCart.subscribe(() => { if (this.isOpen) this._render(); });
    }

    get isOpen() { return this.classList.contains('is-open'); }

    async open() {
      await this._render();
      this.hidden = false;
      // next frame so the transition runs
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.documentElement.style.overflow = 'hidden';
        this.panel && this.panel.focus();
      });
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      const done = () => { this.hidden = true; this.panel && this.panel.removeEventListener('transitionend', done); };
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce || !this.panel) done();
      else this.panel.addEventListener('transitionend', done);
    }

    /* Pull fresh drawer HTML from the Section Rendering API. */
    async _render() {
      try {
        const url = `${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`;
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        if (data['cart-drawer'] && this.content) {
          // The section response wraps content in the section shell; extract the body.
          const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          const inner = doc.querySelector('[data-drawer-content]') || doc.body;
          this.content.innerHTML = inner.innerHTML;
          this._wireContent();
        }
      } catch (e) { console.error('drawer render failed', e); }
    }

    _wireContent() {
      if (!this.content) return;

      // Line quantity: steppers dispatch change on the input; the input's change
      // fires /cart/change.js. The generic stepper delegation (below) bumps the value.
      this.content.querySelectorAll('[data-line-qty]').forEach((input) => {
        input.addEventListener('change', () => this._changeLine(input));
      });
      this.content.querySelectorAll('[data-line-up], [data-line-down]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const line = btn.dataset.line;
          const input = this.content.querySelector(`[data-line-qty][data-line="${line}"]`);
          if (!input) return;
          const delta = btn.hasAttribute('data-line-up') ? 1 : -1;
          input.value = Math.max(0, parseInt(input.value || '0', 10) + delta);
          this._changeLine(input);
        });
      });

      // Gift note -> cart attribute, debounced.
      const note = this.content.querySelector('[data-gift-note]');
      if (note) {
        let t;
        note.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => WyrdoCart.updateAttributes({ gift_note: note.value }), 500);
        });
      }

      // Quick-add packs in the free-shipping pop-up (each a /cart/add form: added in place here instead).
      this.content.querySelectorAll('[data-quick-add]').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          btn.classList.add('is-busy'); btn.disabled = true;
          try { await WyrdoCart.add(btn.dataset.quickAdd, 1); await this._render(); }
          catch (e) { console.error(e); btn.disabled = false; }
          finally { btn.classList.remove('is-busy'); }
        });
      });

      // a capped line (a locked pack is one personal copy: max="1") that came back over its cap — Buy pressed twice,
      // a hand-built link — is brought back down; the drawer renders after every add, so this is the net.
      // One correction at a time: the add's refresh and the open() render both land here with the same over-cap HTML.
      this.content.querySelectorAll('[data-line-qty][max]').forEach((input) => {
        if (!this._capping && parseInt(input.value, 10) > parseInt(input.max, 10)) this._capping = this._changeLine(input).finally(() => { this._capping = null; });
      });
    }

    async _changeLine(input) {
      const line = parseInt(input.dataset.line, 10);
      let qty = Math.max(0, parseInt(input.value || '0', 10));
      if (input.max) qty = Math.min(qty, parseInt(input.max, 10));   // typed values included
      await WyrdoCart.change(line, qty);
      await this._render();
    }
  }
  customElements.define('wyrdo-cart-drawer', WyrdoCartDrawer);

  /* ----------------------------------------------------------------------
   * <wyrdo-sticky-atc> — mobile sticky add-to-cart bar (ST2.5). Appears once the
   * real buy row scrolls out of view. transform/opacity only (no CLS). Mirrors the
   * live price and delegates the tap to the main product form's ATC.
   * -------------------------------------------------------------------- */
  class WyrdoStickyAtc extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.wyrdo-product') || document;
      this.targetSel = this.dataset.observe || '.wyrdo-product__buyrow';
      this.target = this.section.querySelector(this.targetSel);
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.btn = this.querySelector('[data-sticky-add]');
      this.mainAtc = this.section.querySelector('[data-atc]');

      // Tap -> click the real ATC (reuses all its logic + AJAX).
      if (this.btn && this.mainAtc) {
        this.btn.addEventListener('click', () => this.mainAtc.click());
      }

      // Keep price + availability in sync with variant changes.
      this.section.addEventListener('wyrdo:variant-change', (e) => {
        const v = e.detail.variant;
        if (v && this.priceEl) this.priceEl.textContent = money(v.price);
        if (this.btn) this.btn.disabled = !(v && v.available);
      });

      if (!this.target || !('IntersectionObserver' in window)) return;
      this.hidden = false;
      this.observer = new IntersectionObserver(
        ([entry]) => this._toggle(!entry.isIntersecting && entry.boundingClientRect.top < 0),
        { threshold: 0 }
      );
      this.observer.observe(this.target);
    }

    _toggle(show) { this.classList.toggle('is-visible', show); }

    disconnectedCallback() { this.observer && this.observer.disconnect(); }
  }
  customElements.define('wyrdo-sticky-atc', WyrdoStickyAtc);

  /* ----------------------------------------------------------------------
   * <wyrdo-unlock> — the code box on a locked pack (product-hero, wyrdo.status = unlock).
   * The code is a real Shopify discount code (a $0 key, or an earned $-off code), so the cart's
   * own discount engine is the judge: put the deck in the cart, send the code with every code
   * already there, and read discount_codes[].applicable back. Anything but a confirmed `true`
   * puts the cart back exactly as it was. A SOFT lock by design (no checkout Functions on Basic).
   * discount_codes is undocumented (Shopify's Horizon theme relies on it too): if it ever goes
   * missing the box fails closed. No-JS: the band stays locked.
   * -------------------------------------------------------------------- */
  const UNLOCK_KEY = 'wyrdo-tt-unlock';
  class WyrdoUnlock extends HTMLElement {
    connectedCallback() {
      this.band = document.querySelector('.wyrdo-product[data-unlock]');
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-unlock-form]');
      this.msg = this.querySelector('[data-unlock-msg]');
      this.variantId = Number(this.dataset.variantId);
      if (!this.band || !this.dialog || !this.form) return;

      // both openers: "I've got a code" while locked, "Got a different code?" once open — a fresh box each time
      document.querySelectorAll('[data-unlock-open]').forEach((btn) => {
        btn.addEventListener('click', () => { this.form.reset(); this._say(''); this.dialog.showModal(); });
      });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.tryCode(this.form.elements.code.value); });
      // the unlocked buy form adds the deck without its key: send the stored code after every add
      document.addEventListener('wyrdo:cart-added', () => { if (this.stored) this.tryCode(this.stored, true); });

      if (this.stored) this.band.dataset.unlock = 'open';

      // ?code=XXXX (the link in the email): run it, and keep the code out of the address bar
      const url = new URL(window.location.href);
      const auto = url.searchParams.get('code');
      if (auto) {
        url.searchParams.delete('code');
        window.history.replaceState({}, '', url);
        this.form.elements.code.value = auto;
        this.dialog.showModal();
        this.tryCode(auto);
      }
    }

    get stored() { try { return JSON.parse(localStorage.getItem(UNLOCK_KEY)).code; } catch (e) { return ''; } }

    _say(text, error) { this.msg.textContent = text; this.msg.classList.toggle('is-error', !!error); }

    /* quiet = the re-send after an add from the unlocked buy form: no dialog, never adds the deck itself. */
    async tryCode(raw, quiet) {
      const code = String(raw || '').trim().toUpperCase();
      if (!/^[A-Z0-9._-]{1,64}$/.test(code)) return this._say(this.dataset.error, true);
      if (this.busy) return;
      this.busy = true;
      if (!quiet) this._say(this.dataset.checking);

      const same = (c) => String(c.code).toUpperCase() === code;
      const stored = String(this.stored).toUpperCase();
      let before = null, added = false, ok = null;   // ok: true / false = the store's answer, null = no answer
      try {
        const cart = await WyrdoCart.get();
        const inCart = cart.items.some((i) => i.variant_id === this.variantId);
        const codes = cart.discount_codes || [];
        if (quiet && (!inCart || codes.some((c) => same(c) && c.applicable))) return;   // another product was added, or the key is already on
        before = codes.filter((c) => !same(c)).map((c) => c.code);   // every other code stays (a PREWYRDO-… must survive this)
        // a swap sends the new code WITHOUT the one it replaces: two product codes never share a line, so the loser would
        // sit on the cart as not-applicable (key → EARNED) or reject the newcomer (EARNED → key). `before` still restores it on failure.
        const send = before.filter((c) => String(c).toUpperCase() !== stored);
        if (!inCart) { await WyrdoCart.add(this.variantId, 1); added = true; }
        const after = await WyrdoCart.update({ discount: send.concat(code).join(',') });
        if (Array.isArray(after.discount_codes)) ok = after.discount_codes.some((c) => same(c) && c.applicable === true);
        else console.warn('wyrdo-unlock: /cart/update.js answered without discount_codes, so the code cannot be verified');
      } catch (e) {
        console.warn('wyrdo-unlock:', e);
      } finally {
        if (!ok && before) {
          // leave the cart exactly as we found it: the deck we added comes out, the previous codes go back
          await WyrdoCart.update(added ? { updates: { [this.variantId]: 0 }, discount: before.join(',') } : { discount: before.join(',') }).catch(() => {});
        }
        this.busy = false;
      }

      if (ok) {
        try { localStorage.setItem(UNLOCK_KEY, JSON.stringify({ code })); } catch (e) {}
        this.band.dataset.unlock = 'open';
        if (!quiet) {
          const line = document.querySelector('[data-unlock-success]');
          if (line) line.hidden = false;
          this.dialog.close();
        }
        document.querySelector('wyrdo-cart-drawer')?.open();   // renders the drawer with the code on it, then opens
      } else if (!quiet) {
        this._say(ok === false ? this.dataset.error : this.dataset.retry, true);
      } else if (ok === false) {
        try { localStorage.removeItem(UNLOCK_KEY); } catch (e) {}   // a dead key locks the pack again
        this.band.dataset.unlock = 'locked';
      }
    }
  }
  customElements.define('wyrdo-unlock', WyrdoUnlock);

  /* Prime subscribers with the current cart on load. */
  // the first count refresh is best-effort: the server-rendered count is already right, so a gated or proxied
  // answer (the CLI dev server's password page, offline) must not surface as an unhandled rejection
  const firstRefresh = () => WyrdoCart.refresh().catch(() => {});
  if (document.readyState !== 'loading') firstRefresh();
  else document.addEventListener('DOMContentLoaded', firstRefresh);
})();
